/* API client. Auth comes from the Supabase session (access token as Bearer); requests
   go to VITE_API_BASE (empty in dev => the Vite proxy). A dev-token path remains for
   local work when VITE_AUTH_DEV=true. */
import { supabase } from "./supabase";

const API_BASE = import.meta.env.VITE_API_BASE || "";
const DEV = import.meta.env.VITE_AUTH_DEV === "true";

export const devToken = () => (DEV ? localStorage.getItem("ac_dev_token") : null);
export const setDevToken = (t) => { if (DEV) localStorage.setItem("ac_dev_token", t.trim()); };
export const clearDevToken = () => localStorage.removeItem("ac_dev_token");

/* Best-effort synchronous "is there a session?" for cosmetic UI (e.g. the calculator's
   sign-in link). Checks the dev token and Supabase's persisted auth token. Not a security
   check — every protected call is still verified server-side. */
export const loggedIn = () => {
  if (devToken()) return true;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token") && localStorage.getItem(k)) return true;
    }
  } catch { /* localStorage unavailable */ }
  return false;
};

async function authHeader() {
  const dev = devToken();
  if (dev) return { Authorization: `Bearer ${dev}` };
  const { data } = await supabase.auth.getSession();
  const tok = data?.session?.access_token;
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export async function signOut() {
  clearDevToken();
  await supabase.auth.signOut();
}

async function req(method, path, body) {
  const r = await fetch(API_BASE + path, {
    method,
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (r.status === 204) return null;
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    // FastAPI validation errors arrive as a list of objects — format, don't mangle
    const msg = Array.isArray(data?.detail)
      ? data.detail.map((d) =>
          `${(d.loc || []).slice(1).join(".") || "request"}: ${d.msg}`).join(" · ")
      : (data?.detail || r.statusText);
    throw Object.assign(new Error(msg), { status: r.status });
  }
  return data;
}
const get = (p) => req("GET", p);
const post = (p, b) => req("POST", p, b);
const del = (p) => req("DELETE", p);

export const api = {
  me: () => get("/v1/me"),
  home: () => get("/v1/home"),
  charts: () => get("/v1/charts"),
  createChart: (b) => post("/v1/charts", b),
  onboard: (b) => post("/v1/onboarding", b),
  geocode: (q) => get(`/v1/geocode?q=${encodeURIComponent(q)}`),
  periods: (id, lot, frm, to, levels = "1,2,3,4") =>
    get(`/v1/charts/${id}/periods?lot=${lot}&frm=${frm}&to=${to}&levels=${levels}`),
  releaseSummary: (id, frm, to) =>
    get(`/v1/charts/${id}/release-summary?frm=${frm}&to=${to}`),
  historySummary: (id, lots) => get(`/v1/charts/${id}/history-summary?lots=${lots}`),
  sky: (frm, to) => get(`/v1/sky?frm=${frm}&to=${to}`),
  profections: (id, frm, to) => get(`/v1/charts/${id}/profections?frm=${frm}&to=${to}`),
  returns: (id, frm, to) => get(`/v1/charts/${id}/returns?frm=${frm}&to=${to}`),
  logs: (frm, to) => get(`/v1/logs${frm ? `?frm=${frm}&to=${to}` : ""}`),
  createLog: (b) => post("/v1/logs", b),
  board: (category, tags) => {
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (tags && tags.length) q.set("tags", tags.join(","));
    const s = q.toString();
    return get(`/v1/board${s ? `?${s}` : ""}`);
  },
  post: (id) => get(`/v1/board/${id}`),
  createPost: (b) => post("/v1/board", b),
  vote: (id) => post(`/v1/board/${id}/vote`),
  unvote: (id) => del(`/v1/board/${id}/vote`),
  report: (id, reason) => post(`/v1/board/${id}/report`, { reason }),
  conversations: () => get("/v1/messages"),
  thread: (name) => get(`/v1/messages/${encodeURIComponent(name)}`),
  send: (to, body) => post("/v1/messages", { to_display_name: to, body }),
  reportDm: (id, reason) => post(`/v1/messages/${id}/report`, { reason }),
  block: (name) => post("/v1/blocks", { display_name: name }),
  follow: (name) => post(`/v1/follow/${encodeURIComponent(name)}`),
  unfollow: (name) => del(`/v1/follow/${encodeURIComponent(name)}`),
  notifications: () => get("/v1/notifications?unread_only=true"),
  readNotifications: () => post("/v1/notifications/read"),
  profile: (b) => post("/v1/profile", b),
  setType: (t) => post("/v1/profile/type", { self_reported_type: t }),
  avatars: () => get("/v1/avatars"),
  setAvatar: (ref) => post("/v1/profile/avatar", { kind: "preset", ref }),
  publicProfile: (name) => get(`/v1/users/${encodeURIComponent(name)}`),
  instruments: () => get("/v1/instruments"),
  submitAssessment: (b) => post("/v1/assessments", b),
  assessments: () => get("/v1/assessments"),
  articles: () => get("/v1/articles"),
  article: (id) => get(`/v1/articles/${id}`),
  modArticles: () => get("/v1/mod/articles"),
  modArticleCreate: (b) => post("/v1/mod/articles", b),
  modArticleUpdate: (id, b) => post(`/v1/mod/articles/${id}`, b),
  modArticleDelete: (id) => del(`/v1/mod/articles/${id}`),
  consents: () => get("/v1/consents"),
  setConsent: (b) => post("/v1/consents", b),
  partnerAttributes: () => get("/v1/partner-attributes"),
  partners: () => get("/v1/partners"),
  createPartner: (b) => post("/v1/partners", b),
  deletePartner: (id) => del(`/v1/partners/${id}`),
  feeds: () => get("/v1/feeds"),
  createFeed: (b) => post("/v1/feeds", b),
  revokeFeed: (t) => post(`/v1/feeds/${t}/revoke`),
  sessionPrep: (id, horizon = 90) => get(`/v1/charts/${id}/session-prep?horizon=${horizon}`),
  saveChartNotes: (id, notes) => post(`/v1/charts/${id}/notes`, { notes }),
  intakeTemplates: () => get("/v1/intake-templates"),
  intakeLinks: () => get("/v1/intake-links"),
  createIntakeLink: (b) => post("/v1/intake-links", b),
  revokeIntakeLink: (t) => post(`/v1/intake-links/${t}/revoke`),
  intakeGet: (t) => get(`/v1/intake/${t}`),                 // public (anonymous client)
  intakeGeocode: (t, q) => get(`/v1/intake/${t}/geocode?q=${encodeURIComponent(q)}`),
  intakeSubmit: (t, b) => post(`/v1/intake/${t}`, b),
  modQueue: () => get("/v1/mod/queue"),
  modResolve: (kind, id, status, note) =>
    post(`/v1/mod/reports/${kind}/${id}/resolve`, { status, note }),
  modRemove: (id, reason) => post(`/v1/mod/posts/${id}/remove`, { reason }),
  modRestore: (id) => post(`/v1/mod/posts/${id}/restore`),
  modPin: (id) => post(`/v1/mod/posts/${id}/pin`),
  modLock: (id) => post(`/v1/mod/posts/${id}/lock`),
  modUnlock: (id) => post(`/v1/mod/posts/${id}/unlock`),
  findingsDigest: () => get("/v1/mod/findings-digest"),
  modNews: () => get("/v1/mod/news"),
  modNewsCreate: (b) => post("/v1/mod/news", b),
  modNewsUpdate: (id, b) => post(`/v1/mod/news/${id}`, b),
  modNewsDelete: (id) => del(`/v1/mod/news/${id}`),
  modUnpin: (id) => post(`/v1/mod/posts/${id}/unpin`),
  modSuspend: (name, tier, reason) =>
    post(`/v1/mod/users/${encodeURIComponent(name)}/suspend`, { tier, reason }),
  modUnsuspend: (name) => post(`/v1/mod/users/${encodeURIComponent(name)}/unsuspend`),
  modHistory: (name) => get(`/v1/mod/users/${encodeURIComponent(name)}/history`),
  modSetTitle: (name, title) =>
    post(`/v1/mod/users/${encodeURIComponent(name)}/title`, { title }),
  modThread: (reportId) => get(`/v1/mod/dm-thread/${reportId}`),
};
